import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import CandidateCard from './CandidateCard'
import OpenDatesCard from './OpenDatesCard'
import type { Candidate, OpenDatesPayload } from '../api'

interface SavedWrite {
  target_id: string
  target_name: string
}

interface Props {
  role: 'user' | 'assistant' | 'status' | 'candidate' | 'open_dates'
  content: string
  saved?: SavedWrite[]
  activeStatus?: boolean
  candidate?: Candidate
  openDates?: OpenDatesPayload
  onApprove?: () => Promise<void>
  onSkip?: () => Promise<void>
  onDelete?: () => Promise<void>
}

export default function MessageBubble({ role, content, saved, activeStatus, candidate, openDates, onApprove, onSkip, onDelete }: Props) {
  const isUser = role === 'user'

  if (role === 'status') {
    const lines = content.split('\n')
    return (
      <div
        style={{
          alignSelf: 'flex-start',
          marginBottom: '10px',
          padding: '2px 0',
          fontSize: '12px',
          lineHeight: '1.6',
          maxWidth: '80%',
        }}
      >
        {lines.map((line, i) => {
          const isLast = i === lines.length - 1
          return (
            <div
              key={i}
              className={activeStatus && isLast ? 'status-shimmer' : undefined}
              style={{ color: activeStatus && isLast ? undefined : 'hsl(60, 3%, 46%)' }}
            >
              {line}
            </div>
          )
        })}
      </div>
    )
  }

  if (role === 'candidate' && candidate && onApprove && onSkip) {
    return (
      <div style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '480px', marginBottom: '12px' }}>
        <CandidateCard
          candidate={candidate}
          onApprove={onApprove}
          onSkip={onSkip}
          onDelete={onDelete}
        />
      </div>
    )
  }

  if (role === 'open_dates' && openDates) {
    return (
      <div style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '520px', marginBottom: '12px' }}>
        <OpenDatesCard data={openDates} />
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '12px',
      }}
    >
      <div
        className={isUser ? undefined : 'md'}
        style={{
          maxWidth: '80%',
          padding: isUser ? '10px 14px' : '4px 0',
          borderRadius: isUser ? '12px 12px 3px 12px' : undefined,
          background: isUser ? 'var(--bubble-user)' : undefined,
          color: 'var(--text)',
          fontSize: '14px',
          lineHeight: '1.5',
          wordBreak: 'break-word',
        }}
      >
        {isUser ? content : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
      {saved && saved.length > 0 && (
        <div
          style={{
            marginTop: '4px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          {saved.map((w) => (
            <span
              key={w.target_id}
              style={{
                fontSize: '11px',
                color: 'var(--success)',
                background: 'rgba(34,197,94,0.1)',
                border: '1px solid rgba(34,197,94,0.3)',
                borderRadius: '10px',
                padding: '2px 8px',
              }}
            >
              &#10003; {w.target_name}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
